import { useContext } from 'react'
import useModal from './useModal'
import useStepApi from './useStepApi'
import useStepper from './useStepper'
import useNavigation from './useNavigation'
import { ScenarioStatesProviderContext } from '../providers/StatesProvider'
import { validateSameScenarioData, validateScenarioFormData } from '../utils/validation'
import { SCENARIO_STEPS, SCENARIO_DEPLOYMENT_TYPES, SCENARIO_OPERATION_TYPES, SCENARIO_STATUS } from '../../constants'
import useHwilApi from './useHwilApi'
import {
    getScenarioSetupStatus,
    isOnboardScheduleGenerateRunning,
    isSatelliteGroupsAddRunning
} from '../utils/satellite-groups-add'

const useFormSubmit = () => {
    const { step } = useNavigation()
    const context = useContext(ScenarioStatesProviderContext)
    if (context === undefined) {
        throw new Error('useFormSubmit must be used within a Scenario States Provider')
    }

    const { formData, loading, scenarioResponse, windowStore } = context
    const { updateScenarioStep } = useStepper()
    const { openConfirmationDialouge, setModals } = useModal()
    const { generateHwilAssets, getRemoteAppConnectionStatus, startSimulation } = useHwilApi()
    const { createScenario, patchSatellite, updateHwilConfiguration, patchGroundStations, updateAttitudeCondition, updateScheduleDetails, postFaultInjection, deployScenario } = useStepApi()

    const onSubmit = () => {
        const setupStatus = getScenarioSetupStatus(scenarioResponse)
        const asyncSetupRunning =
            formData.assets.schedule.status === 'running' ||
            isSatelliteGroupsAddRunning(setupStatus) ||
            isOnboardScheduleGenerateRunning(setupStatus)
        if (loading.execution || asyncSetupRunning) return

        const validate = validateScenarioFormData(step as string, formData)
        const checkSameData = validateSameScenarioData(step as string, formData, scenarioResponse)
        const openConfirmationModal = ![SCENARIO_STEPS.SCHEDULE, SCENARIO_STEPS.FAULTS, SCENARIO_STEPS.INITIAL_ALTITUDE].includes(step as string)
        const scenarioDeploymentType = formData.details.scenarioDeploymentType
        const scenarioOperationType = formData.details.scenarioOperation

        const assetNextStep = scenarioResponse?.status === SCENARIO_STATUS.WAITING_FOR_CONNECTION ? SCENARIO_STEPS.RECONNECT : SCENARIO_STEPS.CONNECT

        if (!validate) return
        else if (!checkSameData && openConfirmationModal) {
            return openConfirmationDialouge()
        } else if (!checkSameData && (step === SCENARIO_STEPS.INITIAL_ALTITUDE && scenarioResponse?.draftMode === 'No')) {
            return openConfirmationDialouge()
        }

        switch (step) {
            case SCENARIO_STEPS.DETAILS:
                return (scenarioResponse && checkSameData) ? updateScenarioStep(SCENARIO_STEPS.SATELLITES) : createScenario()
            case SCENARIO_STEPS.SATELLITES:
                const hasSatelliteGroups = (scenarioResponse?.satelliteGroups && scenarioResponse?.satelliteGroups?.length > 0) ? true : false
                const satNextStep = scenarioDeploymentType === SCENARIO_DEPLOYMENT_TYPES.HARDWARE_IN_LOOP ? SCENARIO_STEPS.HWIL : scenarioOperationType === SCENARIO_OPERATION_TYPES.LEOP ? SCENARIO_STEPS.INITIAL_ALTITUDE : SCENARIO_STEPS.SEGMENTS
                return (hasSatelliteGroups && checkSameData) ? updateScenarioStep(satNextStep) : patchSatellite()
            case SCENARIO_STEPS.INITIAL_ALTITUDE:
                const hasOrientation = scenarioResponse?.orientation ? true : false
                const hasAngularRate = scenarioResponse?.angularRate ? true : false
                return (hasOrientation && hasAngularRate && checkSameData) ? updateScenarioStep(SCENARIO_STEPS.SCHEDULE) : updateAttitudeCondition()
            case SCENARIO_STEPS.HWIL:
                const hasHwilinfo = (scenarioResponse?.hwInfo && scenarioResponse?.hwInfo?.length > 0) ? true : false
                return (hasHwilinfo && checkSameData) ? updateScenarioStep(SCENARIO_STEPS.SEGMENTS) : updateHwilConfiguration()
            case SCENARIO_STEPS.SEGMENTS:
                const hasStations = (scenarioResponse?.groundStations && scenarioResponse?.groundStations?.length > 0) ? true : false
                const segmentNextStep = scenarioDeploymentType === SCENARIO_DEPLOYMENT_TYPES.TRUETWIN_COMMAND_CENTER ? SCENARIO_STEPS.DEPLOY : SCENARIO_STEPS.SCHEDULE
                return (hasStations && checkSameData) ? updateScenarioStep(segmentNextStep) : patchGroundStations()
            case SCENARIO_STEPS.FEATURES:
                return updateScenarioStep(SCENARIO_STEPS.SCHEDULE)
            case SCENARIO_STEPS.SCHEDULE:
                const scheduleNextStep = SCENARIO_STEPS.FAULTS
                const hasSchedule = formData?.assets.schedule?.status === 'completed' ? true : false
                const presimReport = windowStore?.modals?.presimReport
                const hasOpenPresimReport = presimReport?.open || presimReport?.pinned || Object.values(presimReport?.windows || {}).some((window) => window.open || window.pinned)
                if ((!hasSchedule || !checkSameData) && hasOpenPresimReport) {
                    return setModals((prev) => ({
                        ...prev,
                        dialouge: {
                            type: 'cancel',
                            title: 'Close Existing Presim Report?',
                            description: 'Generating a new schedule will close the currently open Presim report because it was generated from the previous schedule. You can generate a new Presim report after the schedule generation is complete.',
                            onConfirm: () => {
                                setModals((prev) => ({
                                    ...prev,
                                    dialouge: null
                                }))
                                updateScheduleDetails()
                            },
                            onCancel: () => {
                                setModals((prev) => ({
                                    ...prev,
                                    dialouge: null
                                }))
                            },
                            confirm_button_text: 'Continue',
                            cancel_button_text: 'Cancel',
                        }
                    }))
                }
                return (hasSchedule && checkSameData) ? updateScenarioStep(scheduleNextStep) : updateScheduleDetails()
            case SCENARIO_STEPS.FAULTS:
                const faultsNextStep = scenarioDeploymentType === SCENARIO_DEPLOYMENT_TYPES.HARDWARE_IN_LOOP ? scenarioResponse?.status === SCENARIO_STATUS.HWIL_CONFIG_GENERATED ? SCENARIO_STEPS.CONNECT : scenarioResponse?.status === SCENARIO_STATUS.WAITING_FOR_CONNECTION ? SCENARIO_STEPS.RECONNECT : SCENARIO_STEPS.ASSETS : SCENARIO_STATUS.READY_FOR_SIMULATION ? SCENARIO_STEPS.DEPLOY : SCENARIO_STEPS.ASSETS
                return checkSameData ? updateScenarioStep(faultsNextStep) : postFaultInjection()
            case SCENARIO_STEPS.ASSETS:
                return scenarioResponse?.status === SCENARIO_STATUS.HWIL_CONFIG_GENERATED ? updateScenarioStep(assetNextStep) : generateHwilAssets()
            case SCENARIO_STEPS.DEPLOY:
                return deployScenario()
            case SCENARIO_STEPS.CONNECT:
                return deployScenario()
            case SCENARIO_STEPS.RECONNECT:
                return getRemoteAppConnectionStatus(true)
            case SCENARIO_STEPS.START_SIMULATION:
                return startSimulation()
            default:
                return true
        }
    }

    return {
        onSubmit
    }
}

export default useFormSubmit
